import { useState, useEffect, useCallback } from 'react';
import { Video, ApiError } from '../types';

interface BestApiItem {
  videoId: string;
  title: string;
  channelId: string;
  channelTitle: string;
  thumbnailUrl: string;
  durationSeconds: number;
  viewCount: number;
  deltaViews: number;
  deltaPerHour: number;
  isShort: boolean;
  lastSnapshotAt: number;
}

interface BestApiResponse {
  region: string;
  categoryId: string;
  period: string;
  generatedAt: number;
  videos: BestApiItem[];
}

interface UseBestResult {
  videos: Video[];
  isLoading: boolean;
  error: string | null;
  fetchedAt: string | null;
  refetch: () => void;
}

function secondsToDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  let r = 'PT';
  if (h > 0) r += `${h}H`;
  if (m > 0) r += `${m}M`;
  if (s > 0 || r === 'PT') r += `${s}S`;
  return r;
}

function mapBest(item: BestApiItem): Video {
  return {
    id: item.videoId,
    title: item.title,
    channelTitle: item.channelTitle,
    channelId: item.channelId,
    thumbnail: item.thumbnailUrl,
    viewCount: item.viewCount,
    publishedAt: new Date(item.lastSnapshotAt * 1000).toISOString(),
    duration: secondsToDuration(item.durationSeconds),
    durationSeconds: item.durationSeconds,
    deltaViews: item.deltaViews,
    deltaPerHour: item.deltaPerHour,
    isShort: item.isShort,
  };
}

/**
 * Fetch "best performers" (top view-gainers) for a region/category over a
 * rolling window (w1..m6). `enabled` gates the request so the app only fetches
 * when a window is actually active.
 */
export function useBest(region: string, category: string, period: string, enabled = true): UseBestResult {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  const fetchBest = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const categoryId = category === '0' ? 'ALL' : category;
      const params = new URLSearchParams({ region, category: categoryId, period, limit: '100' });
      const response = await fetch(`/api/best?${params.toString()}`);
      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to fetch best performers');
      }
      const data = (await response.json()) as BestApiResponse;
      setVideos(data.videos.map(mapBest));
      setFetchedAt(new Date(data.generatedAt).toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [region, category, period, enabled]);

  useEffect(() => {
    fetchBest();
  }, [fetchBest]);

  return { videos, isLoading, error, fetchedAt, refetch: fetchBest };
}
